import {
  Box,
  Divider,
  Flex,
  Grid,
  Heading,
  HStack,
  Select,
  Spinner,
  Text,
  VStack
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useIsMobile from '@hooks/useIsMobile';
import ContentLayout from '@layouts/ContentLayout';
import {
  getSardineOnHoldOrders,
  clearSardineOnHoldOrders
} from '@store/actions/sardineOnHoldActions';
import { FILTER_BY_PAST_HOURS, ORDER_RESOURCE } from '@constants';
import OrderDetailsCard from '@components/OrderDetailsCard';
import OrdersList from '@components/OrdersList';
import { useNavigate } from 'react-router-dom';
import { updateSardineOnHoldReqData } from '@store/actions/reqDataActions';

const SardineOnHoldOrders = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const sardineOnHoldOrders = useSelector(
    (state) => state.sardineOnHold.sardineOnHoldOrders
  );
  const { orders, totalPages, isLoading, isError, error } = sardineOnHoldOrders;
  const searchReqData =
    useSelector((state) => state.reqData.sardineOnHoldManager) || {};

  const isMobile = useIsMobile();
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchSardineOnHoldOrders(true);
  }, [searchReqData.ordersInPastHours, searchReqData.pageNo]);

  useEffect(() => {
    return () => {
      dispatch(clearSardineOnHoldOrders());
    };
  }, [dispatch]);

  const fetchSardineOnHoldOrders = (refresh = false) => {
    if (refresh || !orders?.length) {
      dispatch(
        getSardineOnHoldOrders({
          searchReqData,
          ordersInPastHours: searchReqData.ordersInPastHours,
          orderSubStatus: searchReqData.orderSubStatus
        })
      );
    }
  };

  const handlePageChange = (pageNo) => {
    const page = Math.max(0, pageNo - 1);
    const newPageNumber = Math.max(0, page);
    if (newPageNumber !== searchReqData.pageNo) {
      dispatch(
        updateSardineOnHoldReqData({
          pageNo: newPageNumber
        })
      );
    }
  };

  const onOrderDetailsClick = useCallback(
    (orderId, userId, orderResource) => {
      if (isMobile)
        navigate(`/order/${orderId}?orderResource=${orderResource}`);

      setSelectedOrder({
        orderId,
        userId,
        orderResource
      });
    },
    [isMobile, navigate]
  );

  const handleOrdersInPastHours = (e) => {
    const ordersInPastHours = e?.target?.value;

    if (ordersInPastHours !== searchReqData.ordersInPastHours) {
      dispatch(
        updateSardineOnHoldReqData({
          ordersInPastHours
        })
      );
    }
  };

  return (
    <ContentLayout>
      <Grid gridTemplateColumns={{ base: '1fr', lg: '7fr 0fr 3fr' }}>
        <VStack
          gap={4}
          overflowY={{ base: 'initial', lg: 'scroll' }}
          overflowX={'hidden'}
          h={{ lg: '90vh' }}
          py={10}
          px={{ base: 4, sm: 10 }}
          alignItems={'stretch'}
          className="scroll"
        >
          <VStack alignItems={'stretch'} gap={4}>
            <Heading size="lg" color="colorPrimary">
              Sardine On Hold Manager
            </Heading>
            <Text color={'whiteAlpha.700'} fontSize={'md'}>
              All the orders will be listed here which have order sub status as
              SARDINE_FAILURE_AWAIT_EXTERNAL_ACTION
            </Text>
            <HStack justifyContent={'flex-start'} gap={4}>
              <Grid
                gridTemplateColumns={'1fr auto'}
                gap={2}
                alignItems={'center'}
              >
                <Text color={'white'} fontSize={'0.95rem'}>
                  Orders stuck for more than{' '}
                </Text>
                <Box maxWidth="15rem">
                  <Select
                    onChange={handleOrdersInPastHours}
                    name={'pastHourOrders'}
                    value={searchReqData.ordersInPastHours || ''}
                    bg="black"
                    color="gray"
                    size={'sm'}
                    rounded={'lg'}
                    _focus={{
                      border: '1px solid #81EBAB',
                      boxShadow: 'none'
                    }}
                  >
                    {FILTER_BY_PAST_HOURS.map((ordersInPastHours) => (
                      <option
                        key={`hours-${ordersInPastHours}`}
                        value={ordersInPastHours}
                      >
                        {!ordersInPastHours
                          ? 'All orders'
                          : ordersInPastHours + ' hours'}
                      </option>
                    ))}
                  </Select>
                </Box>
              </Grid>
            </HStack>
          </VStack>
          <Divider style={{ margin: '0' }} borderColor="whiteAlpha.300" />
          {isLoading ? (
            <Flex justifyContent="center" alignItems="center" w="full" h="full">
              <Spinner thickness="4px" size="lg" color="colorPrimary" />
            </Flex>
          ) : isError ? (
            <Box
              p={4}
              borderRadius="xl"
              bg={'backGround'}
              borderWidth="1px"
              borderColor={'whiteAlpha.300'}
            >
              <Text color="white" fontSize="md">
                {error}
              </Text>
            </Box>
          ) : orders?.length > 0 ? (
            <OrdersList
              orders={orders}
              pageNo={searchReqData.pageNo || 0}
              totalPages={totalPages}
              handlePageChange={handlePageChange}
              onOrderDetailsClick={onOrderDetailsClick}
              show={'user'}
              orderResource={ORDER_RESOURCE.GOMS_SERVICE}
            />
          ) : (
            <Box
              p={4}
              borderRadius="xl"
              bg={'backGround'}
              borderWidth="1px"
              borderColor={'whiteAlpha.300'}
            >
              <Text color="white" fontSize="md">
                No Sardine On Hold Orders Found!
              </Text>
            </Box>
          )}
        </VStack>

        <Divider
          style={{ margin: '0' }}
          borderColor="whiteAlpha.300"
          orientation="vertical"
        />

        {!isMobile && (
          <Box m={0} alignSelf={'flex-start'}>
            {selectedOrder && (
              <OrderDetailsCard
                userId={selectedOrder.userId}
                orderId={selectedOrder.orderId}
                orderResource={selectedOrder.orderResource}
                isSidebar
                showNavLink
              />
            )}
          </Box>
        )}
      </Grid>
    </ContentLayout>
  );
};

export default SardineOnHoldOrders;
